import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';

const STAGES = ['reported','triaged','quoted','approved','scheduled','in_progress','completed','invoiced','closed','rejected'];
const stageColor = s => ({
    reported:'bg-gray-100', triaged:'bg-blue-100', quoted:'bg-yellow-100',
    approved:'bg-green-100', in_progress:'bg-purple-100',
    completed:'bg-green-200', closed:'bg-gray-200', rejected:'bg-red-100',
}[s] || 'bg-gray-100');

const Maintenance = () => {
    const [rows, setRows] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [detail, setDetail] = useState(null);
    const load = async () => {
        try {
            const [r, v] = await Promise.all([
                axios.get('/api/maintenance'),
                axios.get('/api/vendors').catch(() => ({ data: { vendors: [] } })),
            ]);
            setRows(r.data.requests || []);
            setVendors(v.data.vendors || []);
        } catch { toast.error('Failed'); }
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <h1 className="text-2xl font-bold">Maintenance</h1>
            <div className="bg-white rounded-xl border">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left"><tr>
                        <th className="p-3">Date</th><th className="p-3">Property</th><th className="p-3">Tenant</th>
                        <th className="p-3">Title</th><th className="p-3">Priority</th><th className="p-3">Vendor</th>
                        <th className="p-3">Stage</th>
                    </tr></thead>
                    <tbody>{rows.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-gray-500">No requests.</td></tr> :
                        rows.map(r => (
                            <tr key={r.id} onClick={() => setDetail(r.id)} className="border-t cursor-pointer hover:bg-gray-50">
                                <td className="p-3">{new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                <td className="p-3">{r.property_name}</td>
                                <td className="p-3">{r.tenant_name}</td>
                                <td className="p-3">{r.title}</td>
                                <td className="p-3 capitalize">{r.priority}</td>
                                <td className="p-3">{r.vendor_name || '—'}</td>
                                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${stageColor(r.workflow_status)}`}>{r.workflow_status}</span></td>
                            </tr>
                        ))}</tbody>
                </table>
            </div>
            {detail && <Detail id={detail} vendors={vendors} onClose={() => setDetail(null)} onChanged={load} />}
        </div>
    );
};

const Detail = ({ id, vendors, onClose, onChanged }) => {
    const [data, setData] = useState(null);
    const [vendor, setVendor] = useState('');
    const [estimate, setEstimate] = useState('');
    const [quote, setQuote] = useState('');
    const [actual, setActual] = useState('');
    const [notes, setNotes] = useState('');

    const load = async () => { const r = await axios.get(`/api/maintenance/${id}`); setData(r.data); };
    useEffect(() => { load(); }, [id]);

    const act = async (path, body) => {
        try { await axios.post(`/api/maintenance/${id}/${path}`, body || {}); toast.success('Done'); load(); onChanged(); }
        catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    };
    if (!data) return null;
    const r = data.request;
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 border-b flex justify-between"><h2 className="font-semibold">{r.title}</h2><button onClick={onClose}>✕</button></div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><div className="text-gray-500">Property</div><div>{r.property_name}</div></div>
                        <div><div className="text-gray-500">Tenant</div><div>{r.tenant_name}</div></div>
                        <div className="col-span-2"><div className="text-gray-500">Description</div><div>{r.description}</div></div>
                        <div><div className="text-gray-500">Stage</div><div className="capitalize">{r.workflow_status}</div></div>
                        <div><div className="text-gray-500">Vendor</div><div>{r.vendor_name || '—'}</div></div>
                        <div><div className="text-gray-500">Quote</div><div>{r.quote_amount ? formatCurrency(r.quote_amount) : '—'}</div></div>
                        <div><div className="text-gray-500">Actual</div><div>{r.actual_cost ? formatCurrency(r.actual_cost) : '—'}</div></div>
                    </div>

                    {r.workflow_status === 'reported' && (
                        <Panel title="Assign vendor">
                            <select className="border rounded-lg px-3 py-2 w-full" value={vendor} onChange={e => setVendor(e.target.value)}>
                                <option value="">Choose vendor…</option>
                                {vendors.map(v => <option key={v.id} value={v.id}>{v.name} ({v.category})</option>)}
                            </select>
                            <input type="number" placeholder="Estimated cost" className="border rounded-lg px-3 py-2 w-full mt-2" value={estimate} onChange={e => setEstimate(e.target.value)} />
                            <button onClick={() => act('assign', { vendor_id: vendor, estimated_cost: estimate || null })} className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg">Assign</button>
                        </Panel>
                    )}
                    {['triaged','quoted','approved','in_progress'].includes(r.workflow_status) && (
                        <Panel title="Submit quote">
                            <input type="number" placeholder="Quote amount" className="border rounded-lg px-3 py-2 w-full" value={quote} onChange={e => setQuote(e.target.value)} />
                            <button onClick={() => act('quote', { quote_amount: quote })} className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg">Save quote</button>
                        </Panel>
                    )}
                    {r.workflow_status === 'quoted' && (
                        <button onClick={() => act('approve')} className="px-4 py-2 bg-green-600 text-white rounded-lg">Approve work</button>
                    )}
                    {['approved','in_progress','quoted'].includes(r.workflow_status) && (
                        <Panel title="Mark completed">
                            <input type="number" placeholder="Actual cost" className="border rounded-lg px-3 py-2 w-full" value={actual} onChange={e => setActual(e.target.value)} />
                            <textarea rows={2} placeholder="Completion notes" className="border rounded-lg px-3 py-2 w-full mt-2" value={notes} onChange={e => setNotes(e.target.value)} />
                            <button onClick={() => act('complete', { actual_cost: actual || null, completion_notes: notes })} className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg">Complete + log expense</button>
                        </Panel>
                    )}
                </div>
            </div>
        </div>
    );
};
const Panel = ({ title, children }) => <div className="border rounded-lg p-3"><div className="font-medium mb-2">{title}</div>{children}</div>;

export default Maintenance;
